/**
 * Quality-tab TanStack Query hooks (Plan 07 Task 7.2).
 *
 * One hook per endpoint, each unwrapping the server envelope and returning
 * the inner payload. Query keys start with `['quality', <endpoint>,
 * …, filtersQuestion(filters)]` so the cache is keyed by the semantic filter
 * set (not the raw URL).
 *
 * `staleTime: 60_000` + `placeholderData: keepPreviousData` — and since the
 * recount rule, the second of those no longer means the previous filter set's
 * answer stays on screen. `useFilteredQuery` reports that window as "nothing
 * counted yet" (hooks/use-filtered-query.ts), so the tab blanks and says so.
 * What `keepPreviousData` still buys: telling a recount apart from a first
 * load, and an instant switch back to a filter set already in the cache.
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import {
  resolveRange,
  filtersQuestion,
  type Filters,
} from '@/lib/filters-schema';
import {
  getQualityByUser,
  getQualityKpis,
  getRatingHistogram,
  getRatingTrend,
  type QualityByUserParams,
} from '@/services/quality-api';
import type {
  QualityByUserRow,
  QualityKpis,
  RatingHistogram,
  RatingTrendPoint,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useQualityKpis(filters: Filters) {
  // Resolved [from, to] joins the query key so a preset like '7d' reliably
  // invalidates when the day rolls over — stripDefaults otherwise drops
  // from/to for non-custom presets, leaving the key identical to yesterday.
  const range = resolveRange(filters);
  return useFilteredQuery<QualityKpis>({
    queryKey: [
      'quality',
      'kpis',
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getQualityKpis(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useRatingTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  const range = resolveRange(filters);
  return useFilteredQuery<RatingTrendPoint[]>({
    queryKey: [
      'v2',
      'quality',
      'rating-trend',
      granularity,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getRatingTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Rating histogram with optional prev-period overlay. The `includePrev` flag
 * participates in the query key so flipping the toggle issues a refetch
 * (backend responds with a larger shape when the flag is on).
 */
export function useRatingHistogram(
  filters: Filters,
  options: { includePrev?: boolean } = {},
) {
  const includePrev = options.includePrev ?? false;
  const range = resolveRange(filters);
  return useFilteredQuery<RatingHistogram>({
    queryKey: [
      'v2',
      'quality',
      'rating-histogram',
      includePrev,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () =>
      (await getRatingHistogram(filters, { includePrev })).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

// bug F3 — `useSafetyRefusalsTrend` was removed along with the Safety
// refusals chart on QualityPage. The backend endpoint and
// `SafetyRefusalsPoint` type remain for future tooling; no frontend page
// consumes the hook anymore.

export function useQualityByUser(
  filters: Filters,
  params: QualityByUserParams = {},
) {
  const range = resolveRange(filters);
  /* `sort` is in the key, and it BLANKS: this endpoint sorts and then takes
     the top 25 (`queries.ts`, `.orderBy(...).limit(...)`), so the top 25 by
     rating are a different SET of users from the top 25 by requests — not the
     same rows reordered (owner's ruling 3, 2026-08-20). The per-table sort
     that arranges a fully-loaded table is a different thing: it never reaches
     a key at all, see `filtersQuestion`. */
  return useFilteredQuery<QualityByUserRow[]>({
    queryKey: [
      'v2',
      'quality',
      'by-user',
      params.limit ?? null,
      params.sort ?? null,
      params.minRatings ?? null,
      range.from,
      range.to,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getQualityByUser(filters, params)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
